import ZonePointType from "../../../enums/robot/rooms/zone-point-type";
import IdTypePoint from "./id-type-point";
import Point, { setPointInRoom } from "./point";
import RedZone, { mapRedZoneApiToRedZone, mapRedZoneToApi, RedZoneApi } from "./red-zone";
import RoomData from "./room-data";
import {
  mapEdgeApiToEdge,
  mapNodeApiToNode,
  mapNodeToNodeApi,
  WaypointEdge,
  WaypointEdgeApi,
  WaypointNode,
  WaypointNodeApi,
} from "./waypoints";

export default interface Gate {
  id: string;
  room: string;
  name: string;
  identifier: string;
  aWaypoint: string;
  bWaypoint: string;
  mWaypoint: string;
  redzone: string;
  aPost: IdTypePoint;
  bPost: IdTypePoint;
}

export interface GateApi {
  id: string;
  room: string;
  name: string;
  identifier: string;
  aWaypoint: string;
  bWaypoint: string;
  mWaypoint: string;
  redzone: string;
  aPost: number[];
  bPost: number[];
}

export const mapGateToApi = (gate: Gate) =>
  ({
    ...gate,
    aPost: [gate.aPost.coordinateX, gate.aPost.coordinateY, gate.aPost.coordinateTheta ?? 0], // the dto REQUIRES a third coordinate
    bPost: [gate.bPost.coordinateX, gate.bPost.coordinateY, gate.bPost.coordinateTheta ?? 0],
  }) as GateApi;

export const mapGateApiToGate = (apiData: GateApi[]) => {
  const gates: Gate[] = [];
  apiData.forEach((gateApi) => {
    const gate = mapSingleGateApiToGate(gateApi);
    if (gate == null) return;
    gates.push(gate);
  });
  return gates;
};

export const mapSingleGateApiToGate = (apiData: GateApi) => {
  if (apiData.aPost.length !== 3 || apiData.bPost.length !== 3) return;
  const gateWithUndefined = {
    ...apiData,
    aWaypoint: apiData.aWaypoint,
    bWaypoint: apiData.bWaypoint,
    mWaypoint: apiData.mWaypoint,
    redzone: apiData.redzone,
    aPost: {
      coordinateX: apiData.aPost[0],
      coordinateY: apiData.aPost[1],
      coordinateTheta: apiData.aPost[2],
      type: ZonePointType.GatePoint,
      id: "aPost_" + apiData.id,
    },
    bPost: {
      coordinateX: apiData.bPost[0],
      coordinateY: apiData.bPost[1],
      coordinateTheta: apiData.bPost[2],
      type: ZonePointType.GatePoint,
      id: "bPost_" + apiData.id,
    },
  };

  return gateWithUndefined;
};

export const GATE_COLOR = "rgb(165,42,42)";

export const copyGate = (gate: Gate) => {
  return {
    ...gate,
    aPost: { ...gate.aPost },
    bPost: { ...gate.bPost },
  } as Gate;
};

export interface GateSet {
  gate: Gate;
  redZone: RedZone;
  waypointSet: {
    edges: WaypointEdge[];
    nodes: WaypointNode[];
  };
}

export interface GateSetApi {
  gate: GateApi;
  redZone: RedZoneApi;
  waypointSet: {
    room: string;
    edges: WaypointEdgeApi[];
    nodes: WaypointNodeApi[];
  };
}

export const mapGateSetToApi = (gateSet: GateSet) =>
  ({
    gate: mapGateToApi(gateSet.gate),
    redZone: mapRedZoneToApi(gateSet.redZone),
    waypointSet: {
      room: gateSet.gate.room,
      edges: gateSet.waypointSet.edges,
      nodes: gateSet.waypointSet.nodes.map((wpn) => mapNodeToNodeApi(wpn)),
    },
  }) as GateSetApi;

export const mapGateSetApiToGateSet = (apiData: GateSetApi) =>
  ({
    gate: mapSingleGateApiToGate(apiData.gate),
    redZone: mapRedZoneApiToRedZone(apiData.redZone),
    waypointSet: {
      edges: apiData.waypointSet.edges.map((wpe) => mapEdgeApiToEdge(wpe)),
      nodes: apiData.waypointSet.nodes.map((wpn) => mapNodeApiToNode(wpn)),
    },
  }) as GateSet;

export const calculateGatePoints = (aPost: Point, bPost: Point, roomData: RoomData) => {
  const mWayPoint = setPointInRoom(
    {
      coordinateX: (aPost.coordinateX + bPost.coordinateX) / 2,
      coordinateY: (aPost.coordinateY + bPost.coordinateY) / 2,
    } as Point,
    roomData,
  );
  const vector = {
    coordinateX: bPost.coordinateX - aPost.coordinateX,
    coordinateY: bPost.coordinateY - aPost.coordinateY,
  } as Point;
  const perpendicularVector = {
    coordinateX: -vector.coordinateY,
    coordinateY: vector.coordinateX,
  } as Point;

  const perpendicularVectorLength = Math.sqrt(perpendicularVector.coordinateX ** 2 + perpendicularVector.coordinateY ** 2);

  // vector len = sq(x² + y²) >= 2 * minDistanceToGate
  const wpDistToGate = 2;

  const waypointVector = {
    coordinateX: perpendicularVector.coordinateX,
    coordinateY: perpendicularVector.coordinateY,
  } as Point;

  const wpAlpha = (2 * wpDistToGate) / perpendicularVectorLength;
  waypointVector.coordinateX = waypointVector.coordinateX * wpAlpha;
  waypointVector.coordinateY = waypointVector.coordinateY  * wpAlpha;

  const rzDistToGate = 1;

  const redZonePerpendicularVec = {
    coordinateX: perpendicularVector.coordinateX,
    coordinateY: perpendicularVector.coordinateY,
  } as Point;

  const redZoneVec = {
    coordinateX: vector.coordinateX,
    coordinateY: vector.coordinateY,
  } as Point;

  const rzFrontAlpha = (4 * rzDistToGate) / perpendicularVectorLength;
  redZonePerpendicularVec.coordinateX = redZonePerpendicularVec.coordinateX * rzFrontAlpha;
  redZonePerpendicularVec.coordinateY = redZonePerpendicularVec.coordinateY * rzFrontAlpha;

  const rzSideAlpha = (6 * rzDistToGate) / perpendicularVectorLength;
  redZoneVec.coordinateX = redZoneVec.coordinateX * rzSideAlpha;
  redZoneVec.coordinateY = redZoneVec.coordinateY * rzSideAlpha;

  const aWayPoint = setPointInRoom(
    {
      coordinateX: mWayPoint.coordinateX + waypointVector.coordinateX / 2,
      coordinateY: mWayPoint.coordinateY + waypointVector.coordinateY / 2,
    } as Point,
    roomData,
  );
  const bWayPoint = setPointInRoom(
    {
      coordinateX: mWayPoint.coordinateX - waypointVector.coordinateX / 2,
      coordinateY: mWayPoint.coordinateY - waypointVector.coordinateY / 2,
    } as Point,
    roomData,
  );
  const redZonePoint1 = setPointInRoom(
    {
      coordinateX: aPost.coordinateX + redZonePerpendicularVec.coordinateX / 4 - redZoneVec.coordinateX / 6,
      coordinateY: aPost.coordinateY + redZonePerpendicularVec.coordinateY / 4 - redZoneVec.coordinateY / 6,
    } as Point,
    roomData,
  );
  const redZonePoint2 = setPointInRoom(
    {
      coordinateX: bPost.coordinateX + redZonePerpendicularVec.coordinateX / 4 + redZoneVec.coordinateX / 6,
      coordinateY: bPost.coordinateY + redZonePerpendicularVec.coordinateY / 4 + redZoneVec.coordinateY / 6,
    } as Point,
    roomData,
  );
  const redZonePoint3 = setPointInRoom(
    {
      coordinateX: bPost.coordinateX - redZonePerpendicularVec.coordinateX / 4 + redZoneVec.coordinateX / 6,
      coordinateY: bPost.coordinateY - redZonePerpendicularVec.coordinateY / 4 + redZoneVec.coordinateY / 6,
    } as Point,
    roomData,
  );
  const redZonePoint4 = setPointInRoom(
    {
      coordinateX: aPost.coordinateX - redZonePerpendicularVec.coordinateX / 4 - redZoneVec.coordinateX / 6,
      coordinateY: aPost.coordinateY - redZonePerpendicularVec.coordinateY / 4 - redZoneVec.coordinateY / 6,
    } as Point,
    roomData,
  );
  return {
    aWayPoint,
    bWayPoint,
    mWayPoint,
    redZonePoint1,
    redZonePoint2,
    redZonePoint3,
    redZonePoint4,
  };
};
